import { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

/**
 * Hook for fetching company-related data
 */
export const useCompanyData = (companyId) => {
  const [employees, setEmployees] = useState([]);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch company employees for speaker selection
   */
  const fetchCompanyEmployees = useCallback(async (companyIdParam) => {
    const idToUse = companyIdParam || companyId;
    if (!idToUse) return [];
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query SearchEmployees(
            $filter: SearchableEmployeeFilterInput
            $sort: [SearchableEmployeeSortInput]
            $limit: Int
            $nextToken: String
          ) {
            searchEmployees(
              filter: $filter
              sort: $sort
              limit: $limit
              nextToken: $nextToken
            ) {
              items {
                id
                user {
                  id
                  firstName
                  lastName
                  middleName
                  title
                  email
                  image {
                    fileName
                    alt
                  }
                }
                department {
                  items {
                    department {
                      name {
                        language
                        text
                      }
                    }
                  }
                }
                role
                status
                image {
                  fileName
                  alt
                }
              }
              nextToken
              total
            }
          }
        `,
        variables: {
          filter: {
            companyEmployeesId: { eq: idToUse },
            or: [
              { status: { eq: 'active' } },
              { status: { eq: 'pending' } }
            ]
          },
          limit: 5000
        },
      });
      
      const employeesList = response.data.searchEmployees.items || [];
      setEmployees(employeesList);
      return employeesList;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-company-employees',
        consoleMessage: 'Error fetching employees:',
        context: { companyId: idToUse }
      });
      setError(error.message || 'Failed to fetch employees');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  /**
   * Fetch company details
   */
  const fetchCompanyDetails = useCallback(async (companyIdParam) => {
    const idToUse = companyIdParam || companyId;
    if (!idToUse) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query GetCompany($id: ID!) {
            getCompany(id: $id) {
              id
              name
              industry
              status
              website
              description {
                language
                text
              }
              image {
                main {
                  fileName
                  alt
                }
                logo {
                  fileName
                  alt
                }
              }
              contact {
                phone
                mobile
                fax
                email
                address {
                  street
                  streetNumber
                  city
                  zip
                  countryCode
                }
              }
              admins
              
              x4AdminId
              companyAdminId
            }
          }
        `,
        variables: { id: idToUse }
      });
      
      const company = response.data.getCompany;
      setCompanyDetails(company);
      return company;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-company-details',
        consoleMessage: 'Error fetching company details:',
        context: { companyId: idToUse }
      });
      setError(error.message || 'Failed to fetch company details');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  /**
   * Search employees by name
   */
  const searchEmployees = useCallback((searchTerm) => {
    if (!searchTerm) return [];
    
    const searchLower = searchTerm.toLowerCase();
    return employees.filter((employee) => {
      const fullName = `${employee.user?.firstName || ''} ${employee.user?.middleName || ''} ${employee.user?.lastName || ''}`.toLowerCase();
      const email = employee.user?.email?.toLowerCase() || '';
      
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [employees]);

  /**
   * Get employee by ID
   */
  const getEmployeeById = useCallback((employeeId) => {
    return employees.find(emp => emp.id === employeeId);
  }, [employees]);

  /**
   * Transform employee to speaker format for the form
   */
  const transformEmployeeToSpeaker = useCallback((employee, type = 'co_speaker') => {
    if (!employee) return null;

    // Get department name from nested structure
    const departmentName = employee.department?.items?.[0]?.department?.name?.[0]?.text || '';

    return {
      id: employee.id,
      employeeId: employee.id,
      firstName: employee.user?.firstName || '',
      lastName: employee.user?.lastName || '',
      middleName: employee.user?.middleName || '',
      title: employee.user?.title || '',
      department: departmentName,
      image: employee.image?.fileName ? employee.image : employee.user?.image,
      type: type,
      speakerType: 'employee'
    };
  }, []);

  /**
   * Transform user to speaker format for the form
   */
  const transformUserToSpeaker = useCallback((user, type = 'co_speaker') => {
    if (!user) return null;

    return {
      id: user.id,
      userId: user.id,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      middleName: user.middleName || '',
      title: user.title || '',
      email: user.email || '',
      department: '', // Users don't have departments
      image: user.image,
      type: type,
      speakerType: 'user'
    };
  }, []);

  /**
   * Search users by name or email
   */
  const searchUsers = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return [];

    setIsLoading(true);
    setError(null);

    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query SearchUsers(
            $filter: SearchableUserFilterInput
            $limit: Int
          ) {
            searchUsers(filter: $filter, limit: $limit) {
              items {
                id
                firstName
                lastName
                middleName
                title
                email
                image {
                  fileName
                  alt
                }
              }
            }
          }
        `,
        variables: {
          filter: {
            or: [
              { firstName: { regexp: `.*${searchTerm.toLowerCase()}.*` } },
              { lastName: { regexp: `.*${searchTerm.toLowerCase()}.*` } },
              { email: { regexp: `.*${searchTerm}.*` } },
            ],
          },
          limit: 20
        }
      });

      return response.data.searchUsers.items || [];
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'search-users',
        consoleMessage: 'Error searching users:',
        context: { searchTerm }
      });
      setError(error.message || 'Failed to search users');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Auto-fetch employees when companyId changes
   */
  useEffect(() => {
    if (companyId) {
      fetchCompanyEmployees();
    }
  }, [companyId, fetchCompanyEmployees]);

  /**
   * Reload employees - wrapper around fetchCompanyEmployees for convenience
   */
  const reloadEmployees = useCallback(() => {
    return fetchCompanyEmployees();
  }, [fetchCompanyEmployees]);

  return {
    employees,
    companyDetails,
    isLoading,
    error,
    fetchCompanyEmployees,
    fetchCompanyDetails,
    searchEmployees,
    searchUsers,
    getEmployeeById,
    transformEmployeeToSpeaker,
    transformUserToSpeaker,
    reloadEmployees
  };
};